import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlayerReward } from '@/domain/rewards';
import { BonusType } from '@/domain/bonuses';
import { formatAmount } from '@/domain/player-balance';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/common/button';
import { Icon } from '@/ui/common/icon';
import { Modal } from '@/ui/common/modal';

export interface AvailableRewardsSheetProps {
  open: boolean;
  rewards: PlayerReward[];
  /** Currently applied reward id (pre-select in the list). */
  selectedId?: number | null;
  currency?: string;
  /**
   * `modal` — mobile bottom sheet (default).
   * `inline` — expands inside the desktop betslip footer (no overlay).
   */
  variant?: 'modal' | 'inline';
  onClose: () => void;
  /** Called with the chosen reward, or `null` when the selection was cleared. */
  onApply: (reward: PlayerReward | null) => void;
  className?: string;
}

function rewardValueLabel(reward: PlayerReward, currency: string): string {
  if (typeof reward.Amount === 'number' && Number.isFinite(reward.Amount)) {
    const amount = formatAmount(reward.Amount);
    return currency ? `${amount} ${currency}` : amount;
  }
  return reward.Type === BonusType.BackupBet ? '1x' : '';
}

function rewardSubtitle(reward: PlayerReward, expireLabel: string): string {
  if (reward.Type === BonusType.BackupBet) {
    return expireLabel
      ? `One voided selection covered · ${expireLabel}`
      : 'One voided selection covered';
  }
  return expireLabel
    ? `Valid on singles & combis · ${expireLabel}`
    : 'Valid on singles & combis';
}

interface RewardsPickerBodyProps {
  titleId: string;
  rewards: PlayerReward[];
  pendingId: number | null;
  currency: string;
  compact?: boolean;
  onToggle: (id: number) => void;
  onClose: () => void;
  onApply: () => void;
  formatExpire: (endDate: string | null | undefined) => string;
}

function RewardsPickerBody({
  titleId,
  rewards,
  pendingId,
  currency,
  compact = false,
  onToggle,
  onClose,
  onApply,
  formatExpire,
}: RewardsPickerBodyProps) {
  const { t } = useTranslation();

  return (
    <>
      <div
        className={cn(
          'flex shrink-0 flex-col gap-2',
          compact ? 'px-0 pb-1' : 'items-center gap-3 px-4 pt-3 pb-2',
        )}
      >
        {!compact ? (
          <div className="h-1 w-10 rounded-full bg-border-subtle" aria-hidden />
        ) : null}
        <div className="flex w-full items-start justify-between gap-3">
          <h2
            id={titleId}
            className={cn(
              'font-bold text-text-primary',
              compact ? 'text-body-m' : 'text-heading-s',
            )}
          >
            {t('AvailableRewards', { defaultValue: 'Available rewards' })}
          </h2>
          <button
            type="button"
            aria-label={t('Close', { defaultValue: 'Close' })}
            onClick={onClose}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-icon-muted transition-colors hover:bg-bg-surface"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <p className="w-full text-body-s text-text-secondary">
          {t('SelectRewardHint', {
            defaultValue:
              'Select a reward to apply on this ticket. Tap again to deselect.',
          })}
        </p>
      </div>

      <ul
        className={cn(
          'flex min-h-0 flex-col gap-2 overflow-y-auto',
          compact ? 'max-h-48 px-0 py-1' : 'flex-1 px-4 py-2',
        )}
      >
        {rewards.map((reward) => {
          const selected = pendingId === reward.Id;
          const isBackup = reward.Type === BonusType.BackupBet;
          const typeLabel = isBackup
            ? t('BackupBet', { defaultValue: 'Backup Bet' })
            : t('FreeBet', { defaultValue: 'Free Bet' });
          const name = reward.Name?.trim() || null;
          // Prefer API name when it differs from the type label; always show type.
          const title = name && name.toLowerCase() !== typeLabel.toLowerCase()
            ? name
            : typeLabel;
          const expire = formatExpire(reward.EndDate);
          return (
            <li key={reward.Id}>
              <button
                type="button"
                onClick={() => onToggle(reward.Id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border-[1.5px] bg-bg-surface p-3 text-left transition-colors',
                  selected
                    ? 'border-action-primary'
                    : 'border-border-subtle hover:border-border-default',
                )}
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-action-primary text-text-on-action">
                  <Icon name="bonus-gift" size={20} className="text-text-on-action" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-body-m font-bold text-text-primary">
                      {title}
                    </p>
                    <span className="shrink-0 rounded-full bg-bg-canvas px-2 py-0.5 text-[10px] leading-none font-bold tracking-wide text-text-secondary uppercase">
                      {typeLabel}
                    </span>
                  </div>
                  <p className="truncate text-body-s text-text-secondary">
                    {rewardSubtitle(reward, expire)}
                  </p>
                </div>
                <span className="shrink-0 text-body-m font-bold text-text-primary">
                  {rewardValueLabel(reward, currency)}
                </span>
                <span
                  aria-hidden
                  className={cn(
                    'flex size-[18px] shrink-0 items-center justify-center rounded-sm text-[14px] leading-none font-bold',
                    selected
                      ? 'bg-action-primary text-text-on-action'
                      : 'border border-border-default bg-transparent',
                  )}
                >
                  {selected ? '✓' : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div
        className={cn(
          'shrink-0',
          compact ? 'pt-2' : 'border-t border-border-subtle p-4',
        )}
      >
        <Button size="lg" fullWidth onClick={onApply}>
          {t('ApplyReward', { defaultValue: 'Apply reward' })}
        </Button>
      </div>
    </>
  );
}

/**
 * Available rewards picker — bottom sheet on mobile, inline panel on desktop.
 */
export function AvailableRewardsSheet({
  open,
  rewards,
  selectedId = null,
  currency = '',
  variant = 'modal',
  onClose,
  onApply,
  className,
}: AvailableRewardsSheetProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const [pendingId, setPendingId] = useState<number | null>(selectedId ?? null);

  useEffect(() => {
    if (open) setPendingId(selectedId ?? null);
  }, [open, selectedId]);

  const formatExpire = (endDate: string | null | undefined): string => {
    if (!endDate) return '';
    const date = new Date(endDate);
    if (!Number.isFinite(date.getTime())) return '';
    try {
      return t('Expires', {
        defaultValue: 'expires {{date}}',
        date: new Intl.DateTimeFormat(i18n.language || undefined, {
          day: 'numeric',
          month: 'short',
        }).format(date),
      });
    } catch {
      return '';
    }
  };

  const toggle = (id: number) => {
    setPendingId((prev) => (prev === id ? null : id));
  };

  const handleApply = () => {
    if (pendingId == null) {
      onApply(null);
      return;
    }
    const reward = rewards.find((r) => r.Id === pendingId) ?? null;
    onApply(reward);
  };

  const body = (
    <RewardsPickerBody
      titleId={titleId}
      rewards={rewards}
      pendingId={pendingId}
      currency={currency}
      compact={variant === 'inline'}
      onToggle={toggle}
      onClose={onClose}
      onApply={handleApply}
      formatExpire={formatExpire}
    />
  );

  if (variant === 'inline') {
    if (!open) return null;
    return (
      <div
        role="region"
        aria-labelledby={titleId}
        className={cn(
          'flex w-full flex-col overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated p-3',
          className,
        )}
      >
        {body}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      className="items-end justify-center p-0 sm:items-center sm:p-4"
      panelClassName={cn(
        'flex w-full max-h-[85dvh] flex-col overflow-hidden rounded-t-2xl bg-bg-elevated',
        'sm:max-w-md sm:rounded-2xl',
      )}
    >
      {body}
    </Modal>
  );
}
